import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTripperScrollContext } from "../context/TripperWidgetContext";
import { useTripperTrack } from "../context/TripperTrackingContext";
import type { TripperData, SegmentBlock, StopBlock } from "../types";

/**
 * Builds a lookup from stop block _id → operator entityId by flattening
 * all segments, including stops nested inside groups.
 */
function buildBlockToOperatorMap(data: TripperData): Map<string, string> {
  const map = new Map<string, string>();

  const processBlock = (block: SegmentBlock) => {
    if (block.blockType === "STOP") {
      const stop = block as StopBlock;
      const entityId = stop.entityRef?.entityId;
      if (entityId) {
        map.set(stop._id, entityId);
      }
    } else if (block.blockType === "GROUP") {
      for (const child of block.items) {
        processBlock(child);
      }
    }
  };

  for (const segment of data.segments) {
    for (const block of segment.items) {
      processBlock(block);
    }
  }

  return map;
}

/**
 * Analytics hook for the Tripper viewer widget.
 *
 * Handles:
 * - Impression dedup (fires entity_impression once per operator per session)
 * - Operator view tracking (details_click)
 * - Outbound click tracking (outbound_click)
 * - Widget view tracking (widget_view, once on mount)
 * - Widget launch tracking (widget_launch, for embed click-to-open)
 */
export const useTripperTracking = (data: TripperData) => {
  const trackPostHogEvent = useTripperTrack();
  const { activeItemId, isMetadataInView } = useTripperScrollContext();

  const blockToOperator = useMemo(() => buildBlockToOperatorMap(data), [data]);
  const seenOperators = useRef(new Set<string>());
  const hasTrackedWidgetView = useRef(false);
  const hasTrackedEngagement = useRef(false);

  // Fire entity_impression when a new stop becomes active, deduplicated per session
  useEffect(() => {
    if (!activeItemId) return;

    const operatorEntityId = blockToOperator.get(activeItemId);
    if (!operatorEntityId) return;
    if (seenOperators.current.has(operatorEntityId)) return;

    seenOperators.current.add(operatorEntityId);
    trackPostHogEvent("entity_impression", { operatorEntityId });
  }, [activeItemId, blockToOperator, trackPostHogEvent]);

  // Fire widget_view once when the widget mounts with data
  useEffect(() => {
    if (hasTrackedWidgetView.current) return;
    if (!data.itinerary) return;

    hasTrackedWidgetView.current = true;
    trackPostHogEvent("widget_view");
  }, [data.itinerary, trackPostHogEvent]);

  // Fire itinerary_engaged once when user scrolls past the header
  useEffect(() => {
    if (hasTrackedEngagement.current) return;
    if (isMetadataInView !== false) return; // wait for explicit false (scrolled past)

    hasTrackedEngagement.current = true;
    trackPostHogEvent("itinerary_engaged");
  }, [isMetadataInView, trackPostHogEvent]);

  const trackOperatorView = useCallback(
    (operatorEntityId: string) => {
      trackPostHogEvent("details_click", { operatorEntityId });
    },
    [trackPostHogEvent],
  );

  const trackOutboundClick = useCallback(
    (operatorEntityId: string, linkType: "google_maps" | "website") => {
      trackPostHogEvent("outbound_click", { operatorEntityId, linkType });
    },
    [trackPostHogEvent],
  );

  const trackWidgetLaunch = useCallback(() => {
    trackPostHogEvent("widget_launch");
  }, [trackPostHogEvent]);

  return {
    trackOperatorView,
    trackOutboundClick,
    trackWidgetLaunch,
  };
};
